import type { ContainerMode } from "./engine-config";

interface Point {
  readonly x: number;
  readonly y: number;
}

export interface ContainerPathOptions {
  readonly mode: ContainerMode;
  readonly time: number;
  readonly baseRadius: number;
  readonly morph: number;
  readonly wobble: number;
}

export function generateContainerPath({
  mode,
  time,
  baseRadius,
  morph,
  wobble,
}: ContainerPathOptions): string {
  const center = 150;
  const pointCount = 12;
  const points: Point[] = [];

  for (let index = 0; index < pointCount; index += 1) {
    const angle = (Math.PI * 2 * index) / pointCount;
    let radius = baseRadius;

    if (mode === "circle") {
      radius *= 1 + Math.sin(time * 1.7 + index) * morph * 0.2;
    } else if (mode === "triangle") {
      radius *=
        (0.92 + ((Math.cos(3 * angle + time * 0.25) + 1) / 2) * 0.19) *
        (1 + Math.sin(time + index) * morph * 0.12);
    } else if (mode === "quad") {
      const bias = Math.max(
        Math.abs(Math.cos(angle + 0.12)),
        Math.abs(Math.sin(angle + 0.12)),
      );
      radius *= 0.87 + bias * 0.16;
    } else if (mode === "bubbling") {
      radius *=
        1 +
        Math.sin(2 * angle + time * 1.2) * morph * 0.36 +
        Math.cos(5 * angle - time * 0.9) * morph * 0.24;
    } else {
      radius *=
        1 +
        Math.sin(3 * angle + time * 0.8) * morph * 0.28 +
        Math.cos(4 * angle - time * 0.4) * morph * 0.18;
    }

    radius *= 1 + Math.sin(time * 2.2 + index * 1.6) * wobble;
    points.push({
      x: center + Math.cos(angle) * radius,
      y: center + Math.sin(angle) * radius,
    });
  }

  return smoothClosedPath(points);
}

function smoothClosedPath(points: readonly Point[]): string {
  const count = points.length;
  let path = "";

  for (let index = 0; index < count; index += 1) {
    const previous = points[(index - 1 + count) % count];
    const current = points[index];
    const next = points[(index + 1) % count];
    const following = points[(index + 2) % count];
    const control1X = current.x + (next.x - previous.x) / 6;
    const control1Y = current.y + (next.y - previous.y) / 6;
    const control2X = next.x - (following.x - current.x) / 6;
    const control2Y = next.y - (following.y - current.y) / 6;

    if (index === 0) {
      path += `M ${current.x.toFixed(2)} ${current.y.toFixed(2)} `;
    }
    path +=
      `C ${control1X.toFixed(2)} ${control1Y.toFixed(2)}, ` +
      `${control2X.toFixed(2)} ${control2Y.toFixed(2)}, ` +
      `${next.x.toFixed(2)} ${next.y.toFixed(2)} `;
  }

  return `${path}Z`;
}
